import {
  FRAME_COUNT_WARNING,
  MAX_DIMENSION,
  MAX_GIF_FPS,
  MIN_DIMENSION,
  MIN_GIF_FPS,
  MIN_TRIM_SECONDS,
} from '@/constants/media';
import {
  type CropRect,
  type GifSettings,
  type QualityPreset,
  type SourceMeta,
  type TrimRange,
} from '@/types';
import { clamp, formatBytes, formatFfmpegTime } from '@/utils/format';

/** Token the worker replaces with the mounted input path. */
export const INPUT_TOKEN = '{{input}}';
export const PALETTE_FILE = 'palette.png';
export const OUTPUT_FILE = 'out.gif';

interface QualityProfile {
  /** palettegen max_colors. */
  maxColors: number;
  /** Width cap applied by the 'auto' size preset. */
  autoWidthCap: number;
  /** Dither chosen when the user leaves dithering on 'auto'. */
  autoDither: 'floyd_steinberg' | 'bayer';
  /** Rough GIF bytes per output pixel per frame, for the size estimate. */
  bytesPerPixelFrame: number;
}

export const QUALITY_PROFILES: Readonly<Record<QualityPreset, QualityProfile>> = {
  high: {
    maxColors: 256,
    autoWidthCap: 640,
    autoDither: 'floyd_steinberg',
    bytesPerPixelFrame: 0.09,
  },
  balanced: {
    maxColors: 160,
    autoWidthCap: 480,
    autoDither: 'floyd_steinberg',
    bytesPerPixelFrame: 0.065,
  },
  compact: { maxColors: 96, autoWidthCap: 360, autoDither: 'bayer', bytesPerPixelFrame: 0.042 },
};

export interface GifPlan {
  width: number;
  height: number;
  fps: number;
  maxColors: number;
  /** paletteuse dither argument, e.g. "dither=floyd_steinberg". */
  ditherArg: string;
  /** Seconds of source consumed, after trimming. */
  trimDuration: number;
  /** Seconds of GIF produced (trim ÷ speed). */
  outputDuration: number;
  frameCount: number;
  estimatedBytes: number;
  loopForever: boolean;
  paletteArgs: string[];
  encodeArgs: string[];
  warnings: string[];
}

function resolveOutputSize(
  srcWidth: number,
  srcHeight: number,
  settings: GifSettings,
  profile: QualityProfile,
): { width: number; height: number } {
  const aspect = srcHeight / srcWidth;
  let width: number;
  let height: number;

  switch (settings.sizePreset) {
    case 'auto':
      width = Math.min(srcWidth, profile.autoWidthCap);
      height = width * aspect;
      break;
    case 'original':
      width = srcWidth;
      height = srcHeight;
      break;
    case '480':
    case '360':
    case '240': {
      width = Math.min(srcWidth, Number(settings.sizePreset));
      height = width * aspect;
      break;
    }
    case 'custom':
      width = settings.customWidth;
      height = settings.lockAspect ? width * aspect : settings.customHeight;
      break;
  }

  width = clamp(Math.round(width), MIN_DIMENSION, MAX_DIMENSION);
  height = clamp(Math.round(height), MIN_DIMENSION, MAX_DIMENSION);
  return { width, height };
}

function resolveDitherArg(settings: GifSettings, profile: QualityProfile): string {
  const mode =
    settings.dither === 'auto'
      ? profile.autoDither
      : settings.dither === 'floyd'
        ? 'floyd_steinberg'
        : settings.dither;
  // Bayer at scale 3 trades a faint crosshatch for markedly better compression.
  return mode === 'bayer' ? 'dither=bayer:bayer_scale=3' : `dither=${mode}`;
}

function buildFilterChain(
  crop: CropRect | null,
  width: number,
  height: number,
  fps: number,
  speed: number,
  dropDuplicates: boolean,
): string {
  const parts: string[] = [];
  // Rebase timestamps after input seeking, then apply the speed change.
  parts.push(speed === 1 ? 'setpts=PTS-STARTPTS' : `setpts=(PTS-STARTPTS)/${speed}`);
  if (crop) {
    parts.push(
      `crop=${Math.round(crop.width)}:${Math.round(crop.height)}:${Math.round(crop.x)}:${Math.round(crop.y)}`,
    );
  }
  parts.push(`scale=${width}:${height}:flags=lanczos`);
  parts.push(`fps=${fps}`);
  if (dropDuplicates) {
    // Dropped frames keep their timestamps, so the GIF muxer writes longer
    // delays for held frames — duration is preserved, bytes are not wasted.
    parts.push('mpdecimate');
  }
  return parts.join(',');
}

/**
 * Build the complete two-pass conversion plan from source metadata and the
 * user's edit. Pure and synchronous by design: every ffmpeg decision lives
 * here where it can be unit-tested, and the worker just executes it.
 */
export function buildGifPlan(
  meta: SourceMeta,
  trim: TrimRange,
  crop: CropRect | null,
  settings: GifSettings,
): GifPlan {
  const profile = QUALITY_PROFILES[settings.quality];
  const warnings: string[] = [];

  const srcWidth = crop ? crop.width : meta.width;
  const srcHeight = crop ? crop.height : meta.height;
  const { width, height } = resolveOutputSize(srcWidth, srcHeight, settings, profile);

  const fps = clamp(Math.round(settings.fps), MIN_GIF_FPS, MAX_GIF_FPS);
  const speed = clamp(settings.speed, 0.25, 4);

  const start = clamp(trim.start, 0, Math.max(0, meta.duration - MIN_TRIM_SECONDS));
  const end = clamp(trim.end, start + MIN_TRIM_SECONDS, meta.duration);
  const trimDuration = end - start;
  const outputDuration = trimDuration / speed;
  const frameCount = Math.max(1, Math.round(outputDuration * fps));

  if (frameCount > FRAME_COUNT_WARNING) {
    warnings.push(
      `${frameCount.toLocaleString()} frames — expect a slow encode and a large file. Trimming or lowering FPS helps.`,
    );
  }
  if (width * height > 800 * 600) {
    warnings.push('Very large frame size for a GIF — most uses look identical at 480 px wide.');
  }

  const chain = buildFilterChain(crop, width, height, fps, speed, settings.dropDuplicates);
  const ditherArg = resolveDitherArg(settings, profile);

  const seekArgs = [
    ...(start > 0.001 ? ['-ss', formatFfmpegTime(start)] : []),
    '-t',
    formatFfmpegTime(trimDuration),
  ];

  // Pass 1 — sample every (filtered) frame and build one optimal palette.
  // stats_mode=diff biases the palette toward pixels that change, which is
  // what the eye tracks in a looping GIF.
  const paletteArgs = [
    '-hide_banner',
    '-loglevel',
    'error',
    ...seekArgs,
    '-i',
    INPUT_TOKEN,
    '-vf',
    `${chain},palettegen=max_colors=${profile.maxColors}:stats_mode=diff`,
    '-an',
    '-sn',
    '-dn',
    '-y',
    PALETTE_FILE,
  ];

  // Pass 2 — map frames through the palette. diff_mode=rectangle re-encodes
  // only the changing region of each frame, which shrinks files further.
  const encodeArgs = [
    '-hide_banner',
    '-loglevel',
    'error',
    ...seekArgs,
    '-i',
    INPUT_TOKEN,
    '-i',
    PALETTE_FILE,
    '-lavfi',
    `[0:v]${chain}[v];[v][1:v]paletteuse=${ditherArg}:diff_mode=rectangle`,
    '-loop',
    settings.loopForever ? '0' : '-1',
    '-an',
    '-sn',
    '-dn',
    '-f',
    'gif',
    '-y',
    OUTPUT_FILE,
  ];

  const duplicateFactor = settings.dropDuplicates ? 0.8 : 1;
  const estimatedBytes = Math.round(
    width * height * frameCount * profile.bytesPerPixelFrame * duplicateFactor,
  );

  return {
    width,
    height,
    fps,
    maxColors: profile.maxColors,
    ditherArg,
    trimDuration,
    outputDuration,
    frameCount,
    estimatedBytes,
    loopForever: settings.loopForever,
    paletteArgs,
    encodeArgs,
    warnings,
  };
}

/** Human summary used by the settings/convert panels. */
export function describePlan(plan: GifPlan): string {
  return `${plan.width}×${plan.height} · ${plan.fps} fps · ${plan.outputDuration.toFixed(1)} s · ≈ ${formatBytes(plan.estimatedBytes)}`;
}

/**
 * Build the args for a short black-bar detection pass: sample ~2 s from
 * inside the trimmed range and let cropdetect report the picture area.
 * The log level is forced back to info because cropdetect reports via the
 * log stream and FFmpeg's log level is global state that persists across
 * exec calls in the same wasm instance — a prior conversion's
 * `-loglevel error` would otherwise silence detection entirely.
 */
export function buildCropDetectArgs(meta: SourceMeta, trim: TrimRange): string[] {
  const trimDuration = Math.max(MIN_TRIM_SECONDS, trim.end - trim.start);
  const sampleLength = Math.min(2, trimDuration);
  const sampleEndLimit = Math.min(meta.duration, trim.end) - sampleLength;
  const sampleStart = clamp(
    trim.start + trimDuration * 0.3,
    trim.start,
    Math.max(trim.start, sampleEndLimit),
  );
  return [
    '-hide_banner',
    '-loglevel',
    'info',
    '-ss',
    formatFfmpegTime(sampleStart),
    '-t',
    formatFfmpegTime(sampleLength),
    '-i',
    INPUT_TOKEN,
    '-vf',
    'cropdetect=limit=24:round=2:reset=0',
    '-f',
    'null',
    '-',
  ];
}

/**
 * Parse cropdetect log output. Returns null when nothing meaningful was
 * found or the detected rect is effectively the full frame.
 */
export function parseCropDetect(
  logLines: readonly string[],
  source: { width: number; height: number },
): CropRect | null {
  let last: CropRect | null = null;
  const re = /crop=(\d+):(\d+):(\d+):(\d+)/;
  for (const line of logLines) {
    const m = re.exec(line);
    if (!m) continue;
    const [, w, h, x, y] = m;
    if (w === undefined || h === undefined || x === undefined || y === undefined) continue;
    last = { width: Number(w), height: Number(h), x: Number(x), y: Number(y) };
  }
  if (!last) return null;
  if (last.width < 32 || last.height < 32) return null;
  const nearFull = last.width >= source.width - 4 && last.height >= source.height - 4;
  if (nearFull) return null;
  return {
    x: clamp(last.x, 0, source.width - 32),
    y: clamp(last.y, 0, source.height - 32),
    width: clamp(last.width, 32, source.width),
    height: clamp(last.height, 32, source.height),
  };
}
